// services/auth.service.ts
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, tap } from 'rxjs';
import {environment} from "../../../environments/environment";

export interface CurrentUser {
  username: string;
  email?: string;
  roles?: string[];
}

interface LoginResponse {
  token: string; // adjust to your backend field name
  // You can add more fields from your backend response and map them to CurrentUser
}

@Injectable({ providedIn: 'root' })
export class AuthService {

  private authApi = environment.endpoints.login;

  private http = inject(HttpClient);

  // Token persistence: either sessionStorage (default) or localStorage when "remember me" is checked
  private storage: Storage = sessionStorage;

  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(this.readUserFromStorage());
  user$ = this.currentUserSubject.asObservable();

  private readUserFromStorage(): CurrentUser | null {
    try {
      const raw = sessionStorage.getItem('currentUser') || localStorage.getItem('currentUser');
      return raw ? (JSON.parse(raw) as CurrentUser) : null;
    } catch {
      return null;
    }
  }

  private writeUserToStorage(user: CurrentUser | null) {
    // Always keep both in sync by clearing the other one
    if (user) {
      this.storage.setItem('currentUser', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('currentUser');
      localStorage.removeItem('currentUser');
    }
  }

  private setStorage(remember: boolean) {
    this.storage = remember ? localStorage : sessionStorage;
  }

  getToken(): string | null {
    // Prefer session token, fallback to persistent
    return sessionStorage.getItem('token') ?? localStorage.getItem('token');
  }

  private setToken(token: string | null) {
    // Keep storages in sync: write to selected storage, clear the other
    if (token) {
      this.storage.setItem('token', token);
      // ensure the other storage does not have stale token
      (this.storage === localStorage ? sessionStorage : localStorage).removeItem('token');
    } else {
      sessionStorage.removeItem('token');
      localStorage.removeItem('token');
    }
  }

  setCurrentUser(user: CurrentUser | null, remember = false) {
    this.setStorage(remember);
    this.currentUserSubject.next(user);
    this.writeUserToStorage(user);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  login(username: string, password: string, remember = false) {
    this.setStorage(remember);
    return this.http
      .post<LoginResponse>(`${this.authApi}`, { username, password })
      .pipe(
        tap((res) => {
          const t = res.token;
          this.setToken(t);
          // If your backend returns user details, map them here; otherwise create a minimal CurrentUser
          const user: CurrentUser = { username, email: username };
          this.setCurrentUser(user, remember);
        })
      );
  }

  logout() {
    this.setCurrentUser(null);
    this.setToken(null);
  }
}
